import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import CartDrawer from "@/components/feature/CartDrawer";
import FilterDrawer, { type FilterOption } from "@/pages/home/components/FilterDrawer";
import ProductListing from "@/pages/home/components/ProductListing";
import { categories as mockCategories, products as mockProducts, sortOptions, type Product } from "@/mocks/products";
import {
  fetchStoreCategories,
  fetchBestSellers,
  fetchCatalogCached,
  isStoreEnabled,
  type StoreCategory,
} from "@/services/store-api";
import { collectionLabels, discountPercent, homeOrder, newestProducts, normalizeText, parseCollection, storeDay } from "@/utils/collections";

const PAGE_SIZE = 12;
const ALL_CATEGORY_ID = "todos";

export default function Home() {
  const [products, setProducts] = useState<Product[]>(isStoreEnabled() ? [] : mockProducts);
  const [categories, setCategories] = useState<FilterOption[]>(
    isStoreEnabled()
      ? [{ id: ALL_CATEGORY_ID, label: "Ver tudo" }]
      : [{ id: ALL_CATEGORY_ID, label: "Ver tudo" }, ...mockCategories.map((c) => ({ id: c.id, label: c.label }))]
  );

  const [catalogError, setCatalogError] = useState("");
  const [loadingCatalog, setLoadingCatalog] = useState(isStoreEnabled());
  const loadCatalog = useCallback((attempt = 0) => {
    setCatalogError("");
    setLoadingCatalog(true);
    // Independentes: os produtos aparecem assim que chegam, e uma falha nas
    // categorias (só um filtro) não derruba a vitrine.
    fetchCatalogCached()
      .then((realProducts: Product[]) => {
        // Mantém todas as peças autorizadas; sem estoque aparece como esgotada.
        setProducts(realProducts);
        setLoadingCatalog(false);
      })
      .catch(() => {
        // Falhas passageiras (ex.: limite de chamadas da API) costumam passar
        // sozinhas: tenta de novo uma vez, em silêncio, antes de mostrar erro.
        if (attempt === 0) {
          window.setTimeout(() => loadCatalog(1), 1500);
          return;
        }
        setCatalogError("Não foi possível carregar os produtos. Tente novamente em alguns instantes.");
        setLoadingCatalog(false);
      });
    fetchStoreCategories()
      .then((realCategories: StoreCategory[]) =>
        setCategories([{ id: ALL_CATEGORY_ID, label: "Ver tudo" }, ...realCategories])
      )
      .catch(() => { /* fica só "Ver tudo" */ });
  }, []);
  useEffect(() => {
    if (isStoreEnabled()) loadCatalog();
  }, [loadCatalog]);

  // Uma seção de filtro para cada atributo real que existir nos produtos
  // (Tamanho, Cor, o que o lojista cadastrar), em vez de uma lista fixa.
  const attributeFilters: Record<string, FilterOption[]> = useMemo(() => {
    const valuesByAttribute: Record<string, Set<string>> = {};
    for (const product of products) {
      for (const [attrName, values] of Object.entries(product.attributes ?? {})) {
        if (!valuesByAttribute[attrName]) valuesByAttribute[attrName] = new Set();
        values.forEach((value) => valuesByAttribute[attrName].add(value));
      }
    }
    return Object.fromEntries(
      Object.entries(valuesByAttribute).map(([attrName, values]) => [
        attrName,
        Array.from(values).map((value) => ({ id: value, label: value })),
      ])
    );
  }, [products]);

  // Categoria e coleção vêm da URL (menu do cabeçalho): /produtos?categoria=ID,
  // ?colecao=novidades (20 mais novos) ou ?colecao=promocoes (peças com desconto).
  const [searchParams] = useSearchParams();
  const collection = parseCollection(searchParams.get("colecao"));
  const searchQuery = (searchParams.get("q") ?? "").trim().slice(0, 80);
  const selectedCategory = collection || searchQuery ? "" : searchParams.get("categoria") ?? ALL_CATEGORY_ID;

  // Mais vendidos: vem da consulta de mais vendidos da loja (a mesma das sugestões).
  const [bestSellers, setBestSellers] = useState<Product[] | null>(null);
  useEffect(() => {
    if (collection !== "vendidos" || bestSellers) return;
    let active = true;
    (isStoreEnabled() ? fetchBestSellers() : Promise.resolve(mockProducts)).then((items) => { if (active) setBestSellers(items); }).catch(() => { if (active) setBestSellers([]); });
    return () => { active = false; };
  }, [collection, bestSellers]);
  const [selectedPrices, setSelectedPrices] = useState<string[]>([]);
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string[]>>({});
  const [sortId, setSortId] = useState("relevancia");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const toggleValue = (list: string[], value: string, setter: (next: string[]) => void) => {
    setter(list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
    setVisibleCount(PAGE_SIZE);
  };

  const toggleAttribute = (attributeName: string, valueId: string) => {
    setSelectedAttributes((prev) => {
      const current = prev[attributeName] ?? [];
      const next = current.includes(valueId)
        ? current.filter((item) => item !== valueId)
        : [...current, valueId];
      return { ...prev, [attributeName]: next };
    });
    setVisibleCount(PAGE_SIZE);
  };

  // Ao trocar de categoria/coleção: volta a ordem padrão (a da própria coleção).
  useEffect(() => {
    setSortId("relevancia");
    setVisibleCount(PAGE_SIZE);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [collection, selectedCategory, searchQuery]);

  const activeCategory = categories.find((category) => category.id === selectedCategory);
  const title = searchQuery ? `Busca: ${searchQuery}` : collection ? collectionLabels[collection] : activeCategory && activeCategory.id !== ALL_CATEGORY_ID ? activeCategory.label : "Roupas Femininas";

  // Novidades: os 20 mais novos. Promoções: só peças com desconto real na loja.
  const day = storeDay();
  const baseProducts = useMemo(() => {
    if (collection === "novidades") return newestProducts(products);
    if (collection === "promocoes") return products.filter((product) => discountPercent(product) > 0);
    if (collection === "vendidos") return bestSellers ?? [];
    // Vitrine: embaralhada a cada dia.
    return homeOrder(products, day);
  }, [products, bestSellers, collection, day]);

  const filtered = useMemo(() => {
    const categoryOk = (product: Product) => {
      if (collection || searchQuery) return true;
      if (selectedCategory === ALL_CATEGORY_ID) return true;
      return (product.categoryIds ?? []).includes(Number(selectedCategory));
    };

    const priceOk = (product: Product) => {
      if (selectedPrices.length === 0) return true;
      return selectedPrices.some((id) => {
        if (id === "p1") return product.price <= 100;
        if (id === "p2") return product.price > 100 && product.price <= 150;
        if (id === "p3") return product.price > 150 && product.price <= 200;
        if (id === "p4") return product.price > 200 && product.price <= 300;
        if (id === "p5") return product.price > 300;
        return true;
      });
    };

    const attributesOk = (product: Product) =>
      Object.entries(selectedAttributes).every(([attrName, selected]) => {
        if (selected.length === 0) return true;
        const values = product.attributes?.[attrName] ?? [];
        return selected.some((value) => values.includes(value));
      });

    const needle = normalizeText(searchQuery);
    const searchOk = (product: Product) => !needle || normalizeText(product.name).includes(needle);
    return baseProducts.filter(
      (product) => categoryOk(product) && searchOk(product) && priceOk(product) && attributesOk(product)
    );
  }, [baseProducts, collection, searchQuery, selectedCategory, selectedPrices, selectedAttributes]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    if (sortId === "menor") list.sort((a, b) => a.price - b.price);
    if (sortId === "maior") list.sort((a, b) => b.price - a.price);
    if (sortId === "novidades") list.sort((a, b) => b.id - a.id);
    return list;
  }, [filtered, sortId]);

  const pageItems = sorted.slice(0, visibleCount);
  const hasMore = visibleCount < sorted.length;
  const loadMore = useCallback(() => setVisibleCount((prev) => prev + PAGE_SIZE), []);

  const hasActiveFilters =
    selectedPrices.length > 0 || Object.values(selectedAttributes).some((list) => list.length > 0);

  const clearFilters = () => {
    setSelectedPrices([]);
    setSelectedAttributes({});
    setVisibleCount(PAGE_SIZE);
  };

  return (
    <div className="bz-page">
      <Header />
      <main className="bz-catalog">
        <header className="bz-catalog-head">
          <h1>{title}</h1>
          <nav aria-label="Categorias">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={category.id === ALL_CATEGORY_ID ? "/produtos" : `/produtos?categoria=${encodeURIComponent(category.id)}`}
                aria-current={!collection && !searchQuery && selectedCategory === category.id ? "page" : undefined}
              >
                {category.label}
              </Link>
            ))}
          </nav>
        </header>

        <div className="bz-toolbar">
          <button type="button" onClick={() => setFiltersOpen(true)}>
            <SlidersHorizontal size={15} strokeWidth={1.5} aria-hidden="true" />
            Filtrar{hasActiveFilters ? " (ativo)" : ""}
          </button>
          <label>
            <span className="sr-only">Ordenar por</span>
            <select value={sortId} onChange={(event) => setSortId(event.target.value)}>
              {sortOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
            </select>
          </label>
        </div>
        <p className="bz-count" role="status">{loadingCatalog ? "Carregando..." : `${filtered.length} ${filtered.length === 1 ? "item" : "itens"}`}</p>

        <ProductListing
          products={pageItems}
          hasMore={hasMore}
          onLoadMore={loadMore}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearFilters}
          loading={loadingCatalog}
          error={catalogError}
          onRetry={() => loadCatalog()}
        />
      </main>

      <FilterDrawer
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        selectedPrices={selectedPrices}
        onPriceToggle={(id) => toggleValue(selectedPrices, id, setSelectedPrices)}
        attributeFilters={attributeFilters}
        selectedAttributes={selectedAttributes}
        onAttributeToggle={toggleAttribute}
        hasActiveFilters={hasActiveFilters}
        onClear={clearFilters}
        resultCount={filtered.length}
      />
      <Footer />
      <CartDrawer />
    </div>
  );
}
